import { useState } from "react"
import PropTypes from "prop-types"
import FilterButton from "./FilterButton"
import FilterBadgeContent from "./FilterBadgeContent"
import { Select, Checkbox } from "components/ui"
import { isBlank, get, pluralize, truncate, cleanString } from "utils/helpers"

export const QUESTION_KIND = "question"

export const questionBadge = (selected, options) => {
  const question = options.find(o => o.id.toString() === selected.value.toString())

  const name = get(question, "name", "")

  const comparison = selected.comparison.split("_").join(" ")

  let details = ""

  if (selected.extra && selected.extra.length > 0) {
    if (selected.comparison === "contains") {
      details = selected.extra
    } else {
      const extraItem = question.choices.find(choice => choice === selected.extra[0])
      const item = truncate(extraItem, 20)

      details = `${item} ${
        selected.extra.length > 1
          ? "and " + pluralize("other", "others", selected.extra.length - 1, true)
          : ""
      }`
    }
  }

  return (
    <FilterBadgeContent
      label="Question"
      value={`"${truncate(name, 20)}" ${comparison} ${details}`}
    />
  )
}

const QuestionOptions = ({ onSubmit, options = [] }) => {
  const [value, setValue] = useState("")
  const [comparison, setComparison] = useState("")
  const [extra, setExtra] = useState([])

  const handleChange = e => {
    setValue(e.target.value)
    setComparison("")
    setExtra([])
  }

  const handleComparisonChange = e => {
    setComparison(e.target.value)
    setExtra([])
  }

  const handleExtraChange = e => {
    setExtra([e.target.value])
  }

  const handleSelect = e => {
    if (e.target.checked) {
      setExtra([...extra, e.target.value])
    } else {
      setExtra(extra.filter(ex => ex !== e.target.value))
    }
  }

  const isButtonDisabled = question => {
    if (isBlank(question)) {
      return true
    }

    if (question.kind === "yesno") {
      return isBlank(value) || isBlank(comparison)
    } else if (question.kind === "dropdown") {
      return comparison === "is_not_answered"
        ? isBlank(value) || isBlank(comparison)
        : isBlank(value) || isBlank(comparison) || isBlank(extra)
    } else {
      return (
        isBlank(value) ||
        isBlank(comparison) ||
        (comparison === "contains" ? isBlank(extra[0]) : false)
      )
    }
  }

  const handleSubmit = () => {
    const newExtra = comparison === "contains" ? extra.map(e => cleanString(e)) : extra

    onSubmit({
      comparison,
      value,
      ...(!isBlank(newExtra) && { extra: newExtra }),
      label: questionBadge({ comparison, value, extra: newExtra }, options),
      kind: QUESTION_KIND
    })

    setComparison("")
    setValue("")
    setExtra([])
  }

  const question = options.find(o => o.id.toString() === value.toString())

  return (
    <>
      <Select
        name="question"
        value={value}
        onChange={handleChange}
        options={[
          { value: "", label: "Select a question" },
          ...options.map(option => ({
            value: option.id,
            label: option.name
          }))
        ]}
      />
      {question && ["text", "paragraph"].includes(question.kind) && (
        <div className="py-2">
          <Select
            name="questionComparison"
            value={comparison}
            onChange={handleComparisonChange}
            options={[
              { value: "", label: "Select comparison" },
              { value: "is_answered", label: "Is answered" },
              { value: "is_not_answered", label: "Is not answered" },
              { value: "contains", label: "Contains" }
            ]}
          />
          {comparison === "contains" && (
            <div className="pt-2">
              <input
                type="text"
                placeholder="Type here..."
                value={extra[0]}
                onChange={handleExtraChange}
              />
            </div>
          )}
        </div>
      )}
      {question && question.kind === "dropdown" && (
        <div className="py-2">
          <Select
            name="questionDropdown"
            value={comparison}
            onChange={handleComparisonChange}
            options={[
              { value: "", label: "Select comparison" },
              { value: "includes_all", label: "Includes all" },
              { value: "includes_any", label: "Includes any" },
              { value: "is_not_answered", label: "Not answered" }
            ]}
          />
          {comparison !== "is_not_answered" && question.choices.length > 0 && (
            <div className="pt-2">
              <h5 className="m-0 label">Include</h5>
              {question.choices.map(choice => (
                <Checkbox
                  key={choice}
                  name={`questionChoice_${choice}`}
                  htmlFor={`questionChoice_${choice}`}
                  text={choice}
                  value={choice}
                  checked={extra.includes(choice)}
                  onSelect={handleSelect}
                  labelClasses="mb-1 ws-n"
                />
              ))}
            </div>
          )}
        </div>
      )}
      {question && question.kind === "singlecheckbox" && (
        <div className="py-2">
          <Select
            name="questionYesNo"
            value={comparison}
            onChange={handleComparisonChange}
            options={[
              { value: "", label: "Select answer" },
              { value: "true", label: "Yes" },
              { value: "is_not_answered", label: "No/Not answered" }
            ]}
          />
        </div>
      )}

      {question && question.kind === "yesno" && (
        <div className="py-2">
          <Select
            name="questionYesNo"
            value={comparison}
            onChange={handleComparisonChange}
            options={[
              { value: "", label: "Select answer" },
              { value: "true", label: "Yes" },
              { value: "false", label: "No" },
              { value: "is_not_answered", label: "Not answered" }
            ]}
          />
        </div>
      )}

      <FilterButton disabled={isButtonDisabled(question)} onSubmit={handleSubmit} />
    </>
  )
}

QuestionOptions.propTypes = {
  onSubmit: PropTypes.func,
  options: PropTypes.array
}

export default QuestionOptions
